import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi, CreateSessionRequest, SendMessageRequest } from '../services/api/endpoints/chat';

// Get all chat sessions
export const useGetSessions = () => {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => chatApi.getSessions().then(res => res.data),
  });
};

// Get a specific chat session
export const useGetSession = (sessionId: string) => {
  return useQuery({
    queryKey: ['sessions', sessionId],
    queryFn: () => chatApi.getSession(sessionId).then(res => res.data),
    enabled: !!sessionId,
  });
};

// Get messages for a specific chat session
export const useGetMessages = (sessionId: string) => {
  return useQuery({
    queryKey: ['sessions', sessionId, 'messages'],
    queryFn: () => chatApi.getMessages(sessionId).then(res => res.data),
    enabled: !!sessionId,
  });
};

// Create a new chat session
export const useCreateSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSessionRequest) =>
      chatApi.createSession(data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
};

// Send a message in a chat session
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: SendMessageRequest }) =>
      chatApi.sendMessage(sessionId, data).then(res => res.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', variables.sessionId, 'messages'] });
    },
  });
};

// Get assistant response
export const useGetAssistantMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => chatApi.getAssistantMessage(sessionId).then(res => res.data),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', sessionId, 'messages'] });
    },
  });
};

// Delete a chat session
export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      chatApi.deleteSession(sessionId).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
};